import type { PropsWithChildren } from 'react';
import type { FlexDirectionProperty } from '@mentimeter/ragnar-styled';
import { alpha } from '@mentimeter/ragnar-colors';
import { Box } from '@mentimeter/ragnar-ui/box';
import { useTranslations } from 'next-intl';
import { useTheme } from '../data-mediation';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../utils/constants';
import { legacyThemeAnimatedBackgroundsMap } from '../misc/themes/map';

interface PresentationCanvasT {
  width?: number | string;
  height?: number | string;
  flexDirection?: FlexDirectionProperty | undefined;
  disableClickEvents?: boolean | undefined;
}

export const PresentationCanvas = ({
  height = CANVAS_HEIGHT,
  width = CANVAS_WIDTH,
  flexDirection,
  slideThemeImage,
  disableClickEvents = false,
  renderBackground = true,
  children,
}: PropsWithChildren<
  PresentationCanvasT & {
    renderBackground?: boolean;
    slideThemeImage: string | undefined;
  }
>) => {
  const { config_id } = useTheme();
  const LegacyMovingBackground =
    config_id && legacyThemeAnimatedBackgroundsMap[config_id];
  const t = useTranslations('presenting.sfinx');

  return (
    <Box
      as="main"
      aria-label={t('presentation_view_a11y')}
      width={width}
      height={height}
      overflow="hidden"
      alignItems="center"
      flexDirection={flexDirection}
      bg="bg"
      extend={({ theme }) => ({
        transition: `background ${theme.durations[2]}s ease`,
        backgroundImage: slideThemeImage
          ? `linear-gradient(
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)},
              ${alpha(theme.colors.bg, theme.backdropAlpha ?? 1)}
              ), url(${slideThemeImage})`
          : undefined,
        backgroundSize: 'cover',
        pointerEvents: disableClickEvents ? 'none' : 'auto',
      })}
      data-testid="presentation-canvas"
    >
      {LegacyMovingBackground !== undefined &&
        !slideThemeImage &&
        renderBackground && (
          <>
            <LegacyMovingBackground />
            <LegacyMovingBackgroundColorOverlay />
          </>
        )}
      {children}
    </Box>
  );
};

const LegacyMovingBackgroundColorOverlay = () => (
  <Box
    bg="bg"
    position="absolute"
    width="100%"
    height="100%"
    top={0}
    left={0}
    zIndex={0}
    extend={({ theme }) => {
      return {
        transition: `all ${theme.durations[2]}s ease`,
        opacity: theme.backdropAlpha,
      };
    }}
  />
);
